/**
 * Global polling guard — installed once at app boot, next to the 401
 * interceptor.
 *
 * Production problem it solves: ~95% of all nginx hits were background
 * polling (peaks of ~230 req/s), including hidden tabs and tabs stuck
 * in a retry loop after a failed response.
 *
 * Two central, app-wide rules:
 *
 * 1. HIDDEN TABS DO NOT POLL.
 *    `window.setInterval` is patched: any timer with a period >= 5 s
 *    (i.e. a data poll, never a UI clock tick) skips its callback while
 *    `document.visibilityState !== 'visible'`. The timer itself keeps
 *    running, so the first tick after the tab is focused fires normally.
 *
 * 2. FAILING GET REQUESTS BACK OFF.
 *    Repeated failures (network error, 429, 5xx) on the same `/api/...`
 *    path put that path into an exponential cooldown (5 s → 10 s → 20 s
 *    … capped at 2 min). 4xx is NOT a failure here: a 401 already logs
 *    the user out via the auth interceptor and a 404/403 is a stable
 *    answer, not an overload signal. While cooling down, GETs to that
 *    path resolve instantly with a synthetic 503 instead of hitting the
 *    server, which turns a tight retry loop into a trickle. Mutating
 *    requests (POST/PUT/PATCH/DELETE) are NEVER blocked — a user action
 *    must always reach the server.
 *
 * 3. `/api/auth/me` CAN NEVER FLOOD.
 *    This one path used to be 95% of all nginx hits. On top of the
 *    schedule-level fixes it now has a hard, app-wide fuse: identical
 *    GETs within `ME_CACHE_MS` are answered from an in-memory copy and
 *    concurrent calls share one in-flight request (single-flight). The
 *    cache is keyed by the Authorization header and dropped as soon as
 *    anything is mutated (any non-GET /api/ call).
 */

const POLL_THRESHOLD_MS = 5000;
const ME_PATH = '/api/auth/me';
const ME_CACHE_MS = 60000;
const BASE_COOLDOWN_MS = 5000;
const MAX_COOLDOWN_MS = 120000;
const FAILURES_BEFORE_COOLDOWN = 2;

// { key, at, status, body } + the shared in-flight promise
let meCache = null;
let meInFlight = null;

const authKeyOf = (init, target) => {
  try {
    const h = (init && init.headers) || (target && target.headers) || {};
    if (typeof Headers !== 'undefined' && h instanceof Headers) return h.get('Authorization') || '';
    return h.Authorization || h.authorization || '';
  } catch (_) { return ''; }
};

const meResponse = () =>
  new Response(meCache.body, {
    status: meCache.status,
    headers: { 'Content-Type': 'application/json', 'X-Local-Cache': 'auth-me' },
  });

const failures = new Map(); // path -> { count, until }

const pathOf = (url) => {
  try { return new URL(url, window.location.origin).pathname; } catch (_) { return ''; }
};

const isVisible = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';

export function installPollingGuard() {
  if (typeof window === 'undefined' || window.__pollingGuardInstalled) return;
  window.__pollingGuardInstalled = true;

  // ---- 1. hidden tabs stop polling -------------------------------------
  const originalSetInterval = window.setInterval.bind(window);
  window.setInterval = (handler, delay, ...rest) => {
    if (typeof handler === 'function' && typeof delay === 'number' && delay >= POLL_THRESHOLD_MS) {
      return originalSetInterval(function guarded(...args) {
        if (!isVisible()) return undefined;
        return handler.apply(this, args);
      }, delay, ...rest);
    }
    return originalSetInterval(handler, delay, ...rest);
  };

  // ---- 2. exponential backoff for failing GETs -------------------------
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const target = args[0];
    const url = typeof target === 'string' ? target : (target && target.url) || '';
    const init = args[1] || {};
    const method = (init.method || (target && target.method) || 'GET').toUpperCase();
    const path = pathOf(url);
    const pollable = method === 'GET' && path.startsWith('/api/');

    // ---- 3. /api/auth/me fuse: 60 s cache + single-flight -------------
    if (path === ME_PATH) {
      if (method !== 'GET') { meCache = null; return originalFetch(...args); }
      const key = authKeyOf(init, target);
      if (meCache && meCache.key === key && Date.now() - meCache.at < ME_CACHE_MS) {
        return meResponse();
      }
      if (meInFlight && meInFlight.key === key) {
        await meInFlight.promise.catch(() => {});
        if (meCache && meCache.key === key) return meResponse();
      }
      const promise = (async () => {
        const res = await originalFetch(...args);
        if (res.ok) {
          const body = await res.clone().text();
          meCache = { key, at: Date.now(), status: res.status, body };
        } else {
          meCache = null;
        }
        return res;
      })();
      meInFlight = { key, promise };
      try {
        return await promise;
      } finally {
        if (meInFlight && meInFlight.promise === promise) meInFlight = null;
      }
    }
    // Any mutation invalidates the cached profile (balance, language,
    // password change, new order …) so the next read is fresh.
    if (method !== 'GET' && path.startsWith('/api/')) meCache = null;

    if (pollable) {
      const state = failures.get(path);
      if (state && state.until > Date.now()) {
        return new Response(
          JSON.stringify({ detail: 'Request throttled locally after repeated failures' }),
          { status: 503, headers: { 'Content-Type': 'application/json' } },
        );
      }
    }

    try {
      const res = await originalFetch(...args);
      if (pollable) {
        if (res.status === 429 || res.status >= 500) bumpFailure(path);
        else failures.delete(path);
      }
      return res;
    } catch (err) {
      if (pollable) bumpFailure(path);
      throw err;
    }
  };
}

function bumpFailure(path) {
  const prev = failures.get(path) || { count: 0, until: 0 };
  const count = prev.count + 1;
  const until = count >= FAILURES_BEFORE_COOLDOWN
    ? Date.now() + Math.min(BASE_COOLDOWN_MS * 2 ** (count - FAILURES_BEFORE_COOLDOWN), MAX_COOLDOWN_MS)
    : 0;
  failures.set(path, { count, until });
}

export default installPollingGuard;
